import { useEffect, useState } from "react";

interface CountdownState {
  input: number;
  deadline: number;
  remaining: number;
}

function start(seconds: number): CountdownState {
  return {
    input: seconds,
    deadline: seconds > 0 ? Date.now() + seconds * 1000 : 0,
    remaining: seconds,
  };
}

/**
 * Seconds left of a wait that started when `retryAfterSeconds` was handed over.
 *
 * The reset happens during render, not in an effect, so the first render
 * already reports the full wait — a caller that hides its lockout at zero would
 * otherwise tear it down again the moment it was set. Each tick recomputes from
 * an absolute deadline rather than decrementing, so a tab that was throttled or
 * backgrounded resumes on the right number.
 */
export function useCountdown(retryAfterSeconds: number | null): number {
  const target =
    retryAfterSeconds != null && retryAfterSeconds > 0
      ? Math.ceil(retryAfterSeconds)
      : 0;

  const [state, setState] = useState<CountdownState>(() => start(target));

  if (state.input !== target) {
    setState(start(target));
  }

  useEffect(() => {
    if (target <= 0) return;

    const id = setInterval(() => {
      setState((current) => {
        const left = Math.max(
          0,
          Math.ceil((current.deadline - Date.now()) / 1000),
        );

        return left === current.remaining ? current : { ...current, remaining: left };
      });
    }, 1000);

    return () => clearInterval(id);
  }, [target]);

  return state.input === target ? state.remaining : target;
}
